/**
 * 1:1 port of agents/document_verification/app/services/glare.py
 * (GlareDetector, 4-method voting system). Thresholds match GlareConfig's
 * defaults exactly — do not retune here independently of the server.
 *
 * Every function takes a `tracker` (see documentQuality.js's MatTracker) and
 * registers every cv.Mat it allocates so the caller can free them all in one
 * `finally` block — OpenCV.js Mats are Emscripten-heap allocated, not GC'd.
 */

// --- Multi-scale high-pass (method 1) ---
const HIGHPASS_KERNELS = [31, 71, 121];
const HIGHPASS_THRESHOLDS = [50, 40, 30];

// --- Texture analysis (method 2) ---
const TEXTURE_KERNEL_SIZE = 15;
const TEXTURE_SMOOTHNESS_THRESH = 8.0;
const TEXTURE_BRIGHTNESS_THRESH = 210;

// --- HSV specular (method 3) ---
const HSV_BRIGHTNESS_THRESH = 225;
const HSV_SATURATION_THRESH = 40;
const HSV_MIN_CLUSTER_PX = 200;

// --- Radial brightness profile (method 4) ---
const RADIAL_BRIGHTNESS_THRESH = 235;
const RADIAL_FALLOFF_MIN = 15.0;
const RADIAL_INNER_RADIUS = 15;
const RADIAL_OUTER_RADIUS = 60;

// --- Document-surface-relative offsets ---
const SURFACE_OFFSET_TEXTURE = 20;
const SURFACE_OFFSET_HSV = 15;

// --- Morphology & filtering ---
const MORPH_KERNEL_SIZE = 9;
const MIN_GLARE_AREA_PX = 200;
const MAX_GLARE_RATIO = 0.10;
const MIN_GLARE_RATIO = 0.0002;

// --- Voting ---
const MIN_VOTES = 2;

// --- Final decision ---
const GLARE_WARNING_PCT = 0.1;
const GLARE_REJECT_PCT = 3.0;

function thresholdMask(cv, src, thresh, invert = false) {
  const raw = new cv.Mat();
  cv.threshold(src, raw, thresh, 255, invert ? cv.THRESH_BINARY_INV : cv.THRESH_BINARY);
  const mask = new cv.Mat();
  raw.convertTo(mask, cv.CV_8UC1);
  raw.delete();
  return mask;
}

function percentile(sortedArr, p) {
  const n = sortedArr.length;
  if (n === 0) return 0;
  const idx = (p / 100) * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedArr[lo];
  const frac = idx - lo;
  return sortedArr[lo] * (1 - frac) + sortedArr[hi] * frac;
}

/** Mirrors GlareDetector._estimate_document_surface_p75. */
function estimateDocumentSurfaceP75(cv, gray, hsv, tracker) {
  const channels = tracker.add(new cv.MatVector());
  cv.split(hsv, channels);
  const s = tracker.add(channels.get(1));
  const v = tracker.add(channels.get(2));

  const grayData = gray.data;
  const sData = s.data;
  const vData = v.data;

  const docPixels = [];
  for (let i = 0; i < grayData.length; i++) {
    if (vData[i] > 150 && sData[i] < 80) {
      docPixels.push(grayData[i]);
    }
  }

  const source = docPixels.length > 100 ? docPixels : Array.from(grayData);
  source.sort((a, b) => a - b);
  return percentile(source, 75);
}

/** Mirrors GlareDetector._method_highpass. */
function methodHighpass(cv, gray, tracker) {
  let combined = tracker.add(cv.Mat.zeros(gray.rows, gray.cols, cv.CV_8UC1));
  for (let i = 0; i < HIGHPASS_KERNELS.length; i++) {
    const ks = HIGHPASS_KERNELS[i];
    const bg = tracker.add(new cv.Mat());
    cv.GaussianBlur(gray, bg, new cv.Size(ks, ks), 0, 0, cv.BORDER_DEFAULT);
    const hp = tracker.add(new cv.Mat());
    cv.subtract(gray, bg, hp);
    const mask = tracker.add(thresholdMask(cv, hp, HIGHPASS_THRESHOLDS[i]));
    const next = tracker.add(new cv.Mat());
    cv.bitwise_or(combined, mask, next);
    combined = next;
  }
  return combined;
}

/** Mirrors GlareDetector._method_texture. */
function methodTexture(cv, gray, docP75, tracker) {
  const ks = TEXTURE_KERNEL_SIZE;
  const grayF = tracker.add(new cv.Mat());
  gray.convertTo(grayF, cv.CV_32F);

  const localMean = tracker.add(new cv.Mat());
  cv.blur(grayF, localMean, new cv.Size(ks, ks));

  const grayFSq = tracker.add(new cv.Mat());
  cv.multiply(grayF, grayF, grayFSq);
  const localMeanSq = tracker.add(new cv.Mat());
  cv.blur(grayFSq, localMeanSq, new cv.Size(ks, ks));

  const meanSq = tracker.add(new cv.Mat());
  cv.multiply(localMean, localMean, meanSq);
  const variance = tracker.add(new cv.Mat());
  cv.subtract(localMeanSq, meanSq, variance);
  cv.threshold(variance, variance, 0, 0, cv.THRESH_TOZERO); // np.clip(x, 0, None)

  const localStd = tracker.add(new cv.Mat());
  cv.sqrt(variance, localStd);

  const smooth = tracker.add(thresholdMask(cv, localStd, TEXTURE_SMOOTHNESS_THRESH, true));
  const bright = tracker.add(thresholdMask(cv, gray, TEXTURE_BRIGHTNESS_THRESH));
  const aboveSurface = tracker.add(thresholdMask(cv, gray, docP75 + SURFACE_OFFSET_TEXTURE));

  const step1 = tracker.add(new cv.Mat());
  cv.bitwise_and(smooth, bright, step1);
  const mask = tracker.add(new cv.Mat());
  cv.bitwise_and(step1, aboveSurface, mask);
  return mask;
}

/** Mirrors GlareDetector._method_hsv. */
function methodHsv(cv, hsv, docP75, tracker) {
  const channels = tracker.add(new cv.MatVector());
  cv.split(hsv, channels);
  const s = tracker.add(channels.get(1));
  const v = tracker.add(channels.get(2));

  const bright = tracker.add(thresholdMask(cv, v, HSV_BRIGHTNESS_THRESH));
  const desat = tracker.add(thresholdMask(cv, s, HSV_SATURATION_THRESH, true));
  const aboveSurface = tracker.add(thresholdMask(cv, v, docP75 + SURFACE_OFFSET_HSV));

  const step1 = tracker.add(new cv.Mat());
  cv.bitwise_and(bright, desat, step1);
  const raw = tracker.add(new cv.Mat());
  cv.bitwise_and(step1, aboveSurface, raw);

  const kernel = tracker.add(cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(5, 5)));
  const opened = tracker.add(new cv.Mat());
  cv.morphologyEx(raw, opened, cv.MORPH_OPEN, kernel);

  const labels = tracker.add(new cv.Mat());
  const stats = tracker.add(new cv.Mat());
  const centroids = tracker.add(new cv.Mat());
  const numLabels = cv.connectedComponentsWithStats(opened, labels, stats, centroids, 8);

  const keep = new Uint8Array(numLabels);
  for (let i = 1; i < numLabels; i++) {
    if (stats.intPtr(i, cv.CC_STAT_AREA)[0] >= HSV_MIN_CLUSTER_PX) keep[i] = 1;
  }

  const filtered = tracker.add(cv.Mat.zeros(opened.rows, opened.cols, cv.CV_8UC1));
  const labelData = labels.data32S;
  const outData = filtered.data;
  for (let i = 0; i < labelData.length; i++) {
    if (keep[labelData[i]]) outData[i] = 255;
  }
  return filtered;
}

function maskedMean(dataArr, maskArr) {
  let sum = 0;
  let count = 0;
  for (let i = 0; i < maskArr.length; i++) {
    if (maskArr[i]) {
      sum += dataArr[i];
      count++;
    }
  }
  return { mean: count > 0 ? sum / count : 0, count };
}

/** Mirrors GlareDetector._method_radial. */
function methodRadial(cv, gray, tracker) {
  const h = gray.rows;
  const w = gray.cols;
  const resultMask = tracker.add(cv.Mat.zeros(h, w, cv.CV_8UC1));

  const blurred = tracker.add(new cv.Mat());
  cv.GaussianBlur(gray, blurred, new cv.Size(21, 21), 0, 0, cv.BORDER_DEFAULT);

  const candidates = tracker.add(thresholdMask(cv, blurred, RADIAL_BRIGHTNESS_THRESH));
  if (cv.countNonZero(candidates) === 0) return resultMask;

  const kernel31 = tracker.add(cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(31, 31)));
  const dilated = tracker.add(new cv.Mat());
  cv.dilate(blurred, dilated, kernel31, new cv.Point(-1, -1), 1, cv.BORDER_CONSTANT, cv.morphologyDefaultBorderValue());

  const blurredData = blurred.data;
  const dilatedData = dilated.data;
  const candidatesData = candidates.data;

  const ys = [];
  const xs = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      if (candidatesData[idx] && blurredData[idx] >= dilatedData[idx]) {
        ys.push(y);
        xs.push(x);
      }
    }
  }
  if (ys.length === 0) return resultMask;

  const n = ys.length;
  const used = new Uint8Array(n);
  const peaks = [];
  for (let i = 0; i < n; i++) {
    if (used[i]) continue;
    const cy = ys[i];
    const cx = xs[i];
    let bestJ = i;
    let bestVal = -1;
    for (let j = 0; j < n; j++) {
      const dy = ys[j] - cy;
      const dx = xs[j] - cx;
      if (Math.sqrt(dy * dy + dx * dx) < RADIAL_OUTER_RADIUS) {
        used[j] = 1;
        const val = blurredData[ys[j] * w + xs[j]];
        if (val > bestVal) {
          bestVal = val;
          bestJ = j;
        }
      }
    }
    peaks.push([ys[bestJ], xs[bestJ]]);
  }

  const grayData = gray.data;
  for (const [cy, cx] of peaks) {
    const innerMask = tracker.add(cv.Mat.zeros(h, w, cv.CV_8UC1));
    cv.circle(innerMask, new cv.Point(cx, cy), RADIAL_INNER_RADIUS, new cv.Scalar(255), -1);
    const inner = maskedMean(grayData, innerMask.data);
    if (inner.count === 0) continue;

    const outerMask = tracker.add(cv.Mat.zeros(h, w, cv.CV_8UC1));
    cv.circle(outerMask, new cv.Point(cx, cy), RADIAL_OUTER_RADIUS, new cv.Scalar(255), -1);
    const ringMask = tracker.add(new cv.Mat());
    cv.subtract(outerMask, innerMask, ringMask);
    const ring = maskedMean(grayData, ringMask.data);
    if (ring.count === 0) continue;

    const falloff = inner.mean - ring.mean;
    if (falloff >= RADIAL_FALLOFF_MIN) {
      let glareRadius = Math.floor(RADIAL_OUTER_RADIUS * Math.min(falloff / 30.0, 2.0));
      glareRadius = Math.max(RADIAL_INNER_RADIUS, Math.min(glareRadius, RADIAL_OUTER_RADIUS * 2));
      cv.circle(resultMask, new cv.Point(cx, cy), glareRadius, new cv.Scalar(255), -1);
    }
  }

  return resultMask;
}

function addVotes(voteMap, maskData) {
  for (let i = 0; i < voteMap.length; i++) {
    if (maskData[i] > 0) voteMap[i] += 1;
  }
}

/**
 * Tiers match GlareDetector.detect. Only "reject" fails a frame: the
 * "warning" tier fires on faded/worn laminate without real specular glare, and
 * must stay in step with ImageQualityService.analyze_quality on the server.
 */
export function classifyGlare(glarePct) {
  let status = "pass";
  if (glarePct >= GLARE_REJECT_PCT) status = "reject";
  else if (glarePct >= GLARE_WARNING_PCT) status = "warning";
  return { hasGlare: status === "reject", status };
}

/**
 * Mirrors GlareDetector.detect(). `bgrMat` must be a BGR (not RGBA) Mat —
 * caller is responsible for the RGBA->BGR conversion (see documentQuality.js).
 */
export function detectGlare(cv, bgrMat, tracker) {
  const h = bgrMat.rows;
  const w = bgrMat.cols;
  const total = h * w;

  const gray = tracker.add(new cv.Mat());
  cv.cvtColor(bgrMat, gray, cv.COLOR_BGR2GRAY);
  const hsv = tracker.add(new cv.Mat());
  cv.cvtColor(bgrMat, hsv, cv.COLOR_BGR2HSV);

  const docP75 = estimateDocumentSurfaceP75(cv, gray, hsv, tracker);

  const maskHp = methodHighpass(cv, gray, tracker);
  const maskTex = methodTexture(cv, gray, docP75, tracker);
  const maskHsv = methodHsv(cv, hsv, docP75, tracker);
  const maskRad = methodRadial(cv, gray, tracker);

  const voteMap = new Uint8Array(total);
  addVotes(voteMap, maskHp.data);
  addVotes(voteMap, maskTex.data);
  addVotes(voteMap, maskHsv.data);
  addVotes(voteMap, maskRad.data);

  const combined = tracker.add(cv.Mat.zeros(h, w, cv.CV_8UC1));
  const combinedData = combined.data;
  for (let i = 0; i < total; i++) {
    if (voteMap[i] >= MIN_VOTES) combinedData[i] = 255;
  }

  const kernel9 = tracker.add(cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(MORPH_KERNEL_SIZE, MORPH_KERNEL_SIZE)));
  const opened = tracker.add(new cv.Mat());
  cv.morphologyEx(combined, opened, cv.MORPH_OPEN, kernel9);
  const closed = tracker.add(new cv.Mat());
  cv.morphologyEx(opened, closed, cv.MORPH_CLOSE, kernel9);

  const labels = tracker.add(new cv.Mat());
  const stats = tracker.add(new cv.Mat());
  const centroids = tracker.add(new cv.Mat());
  const numLabels = cv.connectedComponentsWithStats(closed, labels, stats, centroids, 8);

  const minArea = Math.max(MIN_GLARE_AREA_PX, total * MIN_GLARE_RATIO);
  const maxArea = total * MAX_GLARE_RATIO;

  let glarePixels = 0;
  for (let i = 1; i < numLabels; i++) {
    const area = stats.intPtr(i, cv.CC_STAT_AREA)[0];
    if (area >= minArea && area <= maxArea) {
      glarePixels += area;
    }
  }

  const glarePct = (glarePixels / total) * 100;
  return { ...classifyGlare(glarePct), glarePct };
}
